import { BaseModal } from './BaseModal'
import { GameStatus } from '../../components/games/Game'
import { useEffect, useState } from 'react'
import { AcademicCapIcon, InformationCircleIcon } from '@heroicons/react/outline'
import { useNavigate } from 'react-router-dom'
import { VALID_GUESSES } from '../../constants/validGuesses'
import { profane, sensitive } from '../../constants/profane'
import * as shuffleSeed from 'shuffle-seed'

type Props = {
  isOpen: boolean
  gameStatus: GameStatus|null
  handleClose: () => void
  setIsInfoModalOpen: (value: boolean) => void
  handleHintClick: (w: string) => void
}


const COMMON_LETTERS = new Set([
  "e",
  "t",
  "a",
  "o",
  "i",
  "n",
  "s",
  "r",
  "h",
  "l",
  "d",
  "c",
  "m",
  "f",
  "u",
])


const commonLetterCount = (w: string) => {
  return new Set(w.split('').filter(c => COMMON_LETTERS.has(c))).size
}

export const HintModal = ({ isOpen, gameStatus, handleClose, setIsInfoModalOpen, handleHintClick }: Props) => {
  const [suggested, setSuggested] = useState<string[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    if(gameStatus) {
      // very common & contains at least N-2 common letters
      const wordLength = gameStatus.solution.word.length;
      let suggestions = [...VALID_GUESSES.keys()]
        .filter((w: string) => w.length === wordLength
          && VALID_GUESSES.get(w)! > 3.5
          && !profane.has(w)
          && !sensitive.has(w)
          && commonLetterCount(w) >= wordLength - 2
          && w.toLowerCase() !== gameStatus.solution.word.toLowerCase()
        )
      suggestions = shuffleSeed.shuffle(suggestions, gameStatus.solution.word)
      setSuggested(suggestions.slice(0, 21))
    }
  }, [gameStatus])

  const openTips = () => {
    handleClose()
    navigate('/tips')
  }

  const onHowToPlay = () => {
    setIsInfoModalOpen(true)
  }

  return (
    <BaseModal title="" isOpen={isOpen} handleClose={handleClose}>
      <div className="flex flex-col items-center dark:text-gray-300">
        {suggested.length > 0 && 
          <><div className="text-lg font-bold">Let's spark your solve!</div>
            <div className="italic">Here are some {gameStatus?.solution.word.length} letter words</div>
            <ul className="grid grid-cols-3 m-8">
              {suggested.map((s) => <li key={s} role="button" className="px-2 hover:text-indigo-700 hover:dark:text-indigo-400" onClick={() => handleHintClick(s)}>{s}</li>)}
            </ul>
          </>
        }
        <div className="italic"> Need more solve ideas? </div>
        <button
          onClick={openTips}
          className="text-gray-900 mt-2 text-white group flex w-64 items-center rounded-md px-4 py-2 title text-lg justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm row-span-1">
          <AcademicCapIcon 
            className="h-6 w-6 mr-2 scale-up cursor-pointer stroke-white"
            aria-label={'tips'}
            role="button"
            tabIndex={0}
          />
          <div className="text-sm text-white">Tips & Tricks</div>
        </button>
        <button
          onClick={onHowToPlay}
          className="text-gray-900 mt-2 text-white group flex w-64 items-center rounded-md px-4 py-2 title text-lg justify-center items-center text-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm row-span-1">
          <InformationCircleIcon
            className="h-6 w-6 mr-2 scale-up cursor-pointer stroke-white"
            aria-label={'info'}
            role="button"
            tabIndex={0}
          />
          <div className="text-sm text-white">How to play</div>
        </button>
      </div>
    </BaseModal>
		)
}
